import { useEffect, useState } from "react";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { auth } from "../../firebase";
import {
  saveDisplayName,
  uploadProfileAvatar,
  refreshCurrentUser,
  deleteCurrentDeveloperAccount,
} from "../../services/profile.service";
import { resizeImageToSquare } from "../../utils/resizeImageToSquare";

export default function useProfileData(navigate) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [displayName, setDisplayName] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  useEffect(() => {
    return onAuthStateChanged(auth, (nextUser) => {
      if (!nextUser) {
        navigate("/login");
        return;
      }
      setUser(nextUser);
      setDisplayName(nextUser.displayName || "");
      setLoading(false);
    });
  }, [navigate]);

  const saveProfile = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    setSaved(false);

    try {
      await saveDisplayName(displayName);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (nextError) {
      setError(nextError.message);
    } finally {
      setSaving(false);
    }
  };

  const uploadAvatar = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    if (!file.type.startsWith("image/")) {
      setUploadError("Please choose an image file.");
      return;
    }

    setUploadError("");
    setUploading(true);

    try {
      const dataUrl = await resizeImageToSquare(file);
      setAvatarPreview(dataUrl);

      const { photoURL } = await uploadProfileAvatar(dataUrl);
      const currentUser = await refreshCurrentUser();
      setUser({ ...currentUser, photoURL });
    } catch (nextError) {
      setUploadError(nextError.message || "Upload failed.");
      setAvatarPreview(null);
    } finally {
      setUploading(false);
      event.target.value = "";
    }
  };

  const logout = async () => {
    await signOut(auth);
    navigate("/login");
  };

  const deleteAccount = async () => {
    if (deleteConfirm !== "delete my account") {
      return;
    }

    setDeleting(true);
    setDeleteError("");

    try {
      await deleteCurrentDeveloperAccount();
      navigate("/");
    } catch (nextError) {
      if (nextError.message?.includes("requires-recent-login")) {
        setDeleteError("Please sign out and sign back in, then try again.");
      } else {
        setDeleteError(nextError.message || "Deletion failed. Please try again.");
      }
      setDeleting(false);
    }
  };

  return {
    user,
    loading,
    displayName,
    setDisplayName,
    saving,
    saved,
    error,
    avatarPreview,
    uploading,
    uploadError,
    deleteConfirm,
    setDeleteConfirm,
    deleting,
    deleteError,
    setDeleteError,
    saveProfile,
    uploadAvatar,
    logout,
    deleteAccount,
  };
}
